import { GalleryVerticalEnd } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ButtonGroup } from '@/src/renderer/components/ui/ButtonGroup'
import { WindowListIcon } from '@/src/renderer/components/icons/WindowListIcon'
import { useWindowMode } from '@/src/renderer/contexts/WindowModeProvider'

export interface WindowModeToggleProps {
  /**
   * Label-less variant: nur die Icons, 40×32 je Segment. Beide Platzierungen
   * nutzen ihn — der Projektkopf seit Figma 4926:265456, der Abschnittskopf
   * schon vorher.
   *
   * Bewusst NUR das Sichtbare: `showShortcuts` hing früher mit dran, sodass
   * „ohne Beschriftung" stillschweigend auch „ohne Kürzel-Tooltip" hieß. Wer
   * das weglassen will, sagt es jetzt ausdrücklich.
   */
  iconOnly?: boolean
  /**
   * Ob die `⌘L` / `⌘S`-Tooltips erscheinen. Default: `true`.
   */
  showShortcuts?: boolean
  /**
   * Scope for the "Liste" action. Omitted (project header) → the whole project
   * enters list mode. Set to a section id (section header) → only that section is
   * shown in list mode, so the user can focus on a single section.
   */
  sectionId?: string
  className?: string
}

/**
 * "Window mode" switch: **Liste** (list) | **Stapel** (stack).
 *
 * Thin configuration of the generic {@link ButtonGroup} primitive — the two
 * window-mode segments with their icons and i18n labels. The segments are
 * momentary buttons (click fires the action and springs back to Rest).
 *
 * Beide Platzierungen zeigen nur die Icons (Figma 4926:265456): der Projektkopf
 * mit Nähe-Effekt und `⌘L`/`⌘S`-Tooltips, der Abschnittskopf ohne beides — er
 * wird selbst erst bei Annäherung eingeblendet.
 */
export function WindowModeToggle({
  iconOnly = false,
  showShortcuts = true,
  sectionId,
  className,
}: WindowModeToggleProps) {
  const { t } = useTranslation()
  const { switchToListMode, switchToStackMode } = useWindowMode()

  // Shown as a tooltip only (label + badge; `mod` = ⌘ on macOS, Strg/Ctrl
  // elsewhere), and only where `showShortcuts` is on — the section header
  // turns it off.

  return (
    <ButtonGroup
      className={className}
      iconOnly={iconOnly}
      aria-label={t('project.windowMode.label')}
      items={[
        {
          value: 'list',
          label: t('project.windowMode.list'),
          icon: <WindowListIcon />,
          ...(showShortcuts ? { shortcut: { kind: 'character', text: 'L', mod: true } as const } : {}),
        },
        {
          value: 'stack',
          label: t('project.windowMode.stack'),
          icon: <GalleryVerticalEnd />,
          ...(showShortcuts ? { shortcut: { kind: 'character', text: 'S', mod: true } as const } : {}),
        },
      ]}
      // "Liste" enters the compact list window mode — scoped to this section when
      // a sectionId is provided, otherwise the whole project. "Stapel" (stack)
      // enters the deck mode, which is always project scope (no section focus).
      onSelect={(value) => {
        if (value === 'list') switchToListMode(sectionId ?? null)
        if (value === 'stack') switchToStackMode()
      }}
    />
  )
}
